import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import io from 'socket.io-client';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import useNfcReader, { nfcUnavailableReason } from '../utils/useNfcReader';
import { isSidebarHidden, setSidebarHidden } from '../utils/sidebarToggle';

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api')
  .replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const thaiDate = (v) => (v ? new Date(v).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : '');

// ล้างจอเองหลังแตะ เพื่อไม่ให้ข้อมูลนักเรียนคนก่อนค้างอยู่ให้คนถัดไปเห็น
// ใช้กับผลที่จบแล้วเท่านั้น (ห้ามออก / บัตรไม่รู้จัก / ยืนยันแล้ว)
// รายการที่ยังรอยืนยันต้องค้างตามอายุตั๋วที่เซิร์ฟเวอร์ส่งมา — ดู resetSoon
const AUTO_CLEAR_MS = 12000;

// กล่องเคาะทุก 30 วิ — เงียบเกินสองรอบถือว่าหลุด เผื่อ ping หายไปหนึ่งครั้งโดยไม่ต้องตกใจ
const DEVICE_STALE_MS = 75000;

const THEME = {
  allow:    { bg: '#0f5132', accent: '#2ecc71', icon: 'ri-checkbox-circle-fill', title: 'อนุญาตให้ออก' },
  return:   { bg: '#664d03', accent: '#ffc107', icon: 'ri-arrow-go-back-fill',   title: 'กลับเข้าโรงเรียน' },
  deny:     { bg: '#58151c', accent: '#ff5c5c', icon: 'ri-close-circle-fill',    title: 'ห้ามออก' },
  unknown:  { bg: '#41464b', accent: '#adb5bd', icon: 'ri-question-fill',        title: 'บัตรไม่รู้จัก' },
  operator: { bg: '#032830', accent: '#0dcaf0', icon: 'ri-user-star-fill',       title: 'บัตรเจ้าหน้าที่' }
};

const PASS_LABEL = { home: 'กลับบ้าน', outing: 'ออกนอกสถานที่' };

// จอหลักคือสมาร์ททีวี HD/FHD ที่ป้อม ยามยืนห่างหลายเมตร ทุกอย่างบนจอจึงคิดขนาดจากความสูงจอจริง
// ไม่ใช้ clamp() ใน CSS เพราะเบราว์เซอร์ในทีวีหลายรุ่นเป็น Chromium ก่อน 79 ซึ่งจะทิ้งทั้งบรรทัดที่มี clamp()
// พอ width/height ของรูปหายไปก็จะกางเป็นขนาดจริงของไฟล์ ดันเนื้อหาที่เหลือตกจอ
// หมุนขนาดรวมทั้งจอ (1 = ขนาดที่คำนวณตรงๆ) ลองที่ป้อมแล้วเต็มจอไปหน่อยจึงถอยลง 5%
const SCREEN_SCALE = .80;

function useScreen() {
  const [box, setBox] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setBox({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return box;
}

// ใครเป็นคนยืนยัน — self คือนักเรียนแตะบัตรตัวเองซ้ำโดยไม่มีใครดูจอ ต้องเห็นได้ชัดว่าต่างกัน
const CONFIRMED_BY_LABEL = { guard: 'ยืนยันด้วยบัตรเจ้าหน้าที่', screen: 'ยืนยันจากหน้าจอ', self: 'นักเรียนยืนยันเอง' };

// จอนี้ประจำประตูไหน — ตัดสินจาก URL ก่อนอื่นเพราะจอที่ป้อมเป็นทีวีที่ bookmark ไว้ครั้งเดียว
// แล้วไม่มีใครไปกดเลือกใหม่บนรีโมท
//   ?display=tv-gate-1  จอที่ลงทะเบียนไว้ — ประตูมาจากทะเบียน แก้ที่หน้าแอดมินได้ทีหลัง
//   ?gate=gate-1        เลือกประตูตรงๆ ใช้กับมือถือของยาม
//   ไม่มีทั้งสอง        ห้องรวม เห็นทุกประตู
const GATE_STORE_KEY = 'guardGateScanGate';

const urlParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || '';
  } catch {
    return '';
  }
};

// มือถือของยามจำประตูที่เลือกไว้ล่าสุด จะได้ไม่ต้องเลือกใหม่ทุกกะ
const storedGate = () => {
  try {
    return localStorage.getItem(GATE_STORE_KEY) || '';
  } catch {
    return '';
  }
};

const JOIN_ERROR = {
  forbidden: 'บัญชีนี้ไม่มีสิทธิ์ดูจอจุดตรวจประตู',
  unknown_display: 'ไม่พบรหัสจอนี้ในทะเบียน — ตรวจลิงก์ที่เปิด หรือเพิ่มจอในหน้าจัดการประตู',
  display_disabled: 'จอนี้ถูกปิดใช้งานจากหน้าจัดการประตู',
  error: 'เข้าห้องรับผลไม่สำเร็จ'
};

// กลับเข้าตามกำหนด vs เลยกำหนด — ยามควรเห็นต่างกันตั้งแต่แวบแรก
const LATE_THEME = { bg: '#5c3d00', accent: '#ffab2e', icon: 'ri-time-fill', title: 'กลับเข้า — เลยกำหนด' };

export default function GuardGateScan() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [device, setDevice] = useState(null);   // กล่อง ESP32 ที่เห็นล่าสุด (null = ยังไม่เคยเห็น)
  const [now, setNow] = useState(Date.now());   // เดินเองเพื่อให้ป้าย "ออนไลน์" หมดอายุได้โดยไม่ต้องรอ event
  const [menuHidden, setMenuHidden] = useState(true);
  const [displayCode] = useState(() => urlParam('display'));
  const [gateCode, setGateCode] = useState(() => urlParam('gate') || storedGate());
  const [joined, setJoined] = useState(null);   // ผลตอบจาก joinGateDisplay { ok, gateCode, gateName, displayName }
  const [gates, setGates] = useState([]);
  const [deadline, setDeadline] = useState(0);  // ตั๋วยืนยันหมดอายุเมื่อไหร่ (0 = ไม่มีอะไรรออยู่)
  const [heldFromDevice, setHeldFromDevice] = useState(0);  // ผลจากกล่องที่พักไว้ตอนยามสแกนด้วยมือถือ
  const clearTimer = useRef(null);
  // อ่านสถานะสแกนใน handler ของ socket ผ่าน ref ไม่ใช่ dependency ของ effect
  // ไม่งั้น socket จะ disconnect/connect ใหม่ทุกครั้งที่กดเริ่ม/ยกเลิกสแกน แล้วต้อง joinGateDisplay ซ้ำ
  const scanningRef = useRef(false);

  const nfcBlocked = nfcUnavailableReason();

  // เวลาค้างจอต้องมาจากอายุตั๋วฝั่งเซิร์ฟเวอร์ ไม่ใช่ค่าคงที่ฝั่งนี้
  // ตอนแรกจอล้างที่ 12 วิ แต่ตั๋วอยู่ได้ 30 วิ ช่วง 12–30 วินาทีจึงเป็นช่องโหว่:
  // จอว่างเปล่าเหมือนไม่มีใครรออยู่ แต่นักเรียนแตะบัตรใบเดิมซ้ำแล้วออกได้เลย
  // โดยยามไม่มีโอกาสเห็นรูปเทียบหน้า (หัวข้อ 8.1 ของแผน) และปุ่มยืนยันสำรองก็หายไปด้วย
  const resetSoon = useCallback((holdMs = AUTO_CLEAR_MS, pending = false) => {
    clearTimeout(clearTimer.current);
    setDeadline(pending ? Date.now() + holdMs : 0);
    clearTimer.current = setTimeout(() => {
      setResult(null); setConfirmed(false); setDeadline(0);
    }, holdMs);
  }, []);

  useEffect(() => () => clearTimeout(clearTimer.current), []);

  // จอนี้ติดผนังป้อมยามและอ่านจากระยะไกล จึงซ่อนเมนูให้ตั้งแต่เปิดหน้า
  // คืนสภาพเดิมตอนออกจากหน้า เพราะคลาสอยู่บน <body> จะติดไปทั้งแอปถ้าไม่คืน
  useEffect(() => {
    const wasHidden = isSidebarHidden();
    setSidebarHidden(true);
    setMenuHidden(true);
    return () => setSidebarHidden(wasHidden);
  }, []);

  // เปลี่ยนประตูแล้ว socket effect จะต่อใหม่เองเพราะ gateCode เป็น dependency
  const changeGate = (code) => {
    try {
      if (code) localStorage.setItem(GATE_STORE_KEY, code);
      else localStorage.removeItem(GATE_STORE_KEY);
    } catch {
      /* โหมดส่วนตัวของเบราว์เซอร์เขียนไม่ได้ — เลือกได้ตามปกติ แค่ไม่ถูกจำไว้ */
    }
    setResult(null); setConfirmed(false); setDeadline(0); setDevice(null);
    setGateCode(code);
  };

  const toggleMenu = () => {
    const next = !menuHidden;
    setSidebarHidden(next);
    setMenuHidden(next);
  };

  // ชื่อประตูสำหรับหัวจอ และตัวเลือกให้ยามสลับประตูจากมือถือ
  useEffect(() => {
    axiosInstance.get('/api/guard/gates')
      .then(res => setGates(res.data || []))
      .catch(() => {});
  }, []);

  // สถานะกล่องตอนเปิดหน้า — ไม่งั้นต้องรอถึง 30 วิ กว่า ping แรกจะมาถึง
  // กรองเฉพาะกล่องของประตูนี้ ไม่งั้นจอประตูหน้าจะขึ้น "ออนไลน์" เพราะกล่องประตูหลังเคาะมา
  useEffect(() => {
    const watching = joined?.gateCode || gateCode;
    axiosInstance.get('/api/guard/gate-device/status')
      .then(res => {
        const latest = (res.data?.devices || [])
          .filter(d => !watching || d.gateCode === watching)
          .sort((a, b) => b.lastSeenAt - a.lastSeenAt)[0];
        if (latest) setDevice({ deviceId: latest.deviceId, at: latest.lastSeenAt });
      })
      .catch(() => {});
  }, [joined, gateCode]);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(tick);
  }, []);

  // เดินวินาทีเฉพาะตอนมีคนรอยืนยันอยู่ ไม่ต้อง re-render ทั้งวันเพื่อนับถอยหลังที่ไม่มีใครดู
  useEffect(() => {
    if (!deadline) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [deadline]);

  // กล่อง ESP32 ยิงเข้าเซิร์ฟเวอร์ ไม่ได้ยิงมาที่จอโดยตรง ผลจึงเดินทางมาทาง socket
  // ห้อง gate-display เป็นห้องเฉพาะของคนที่มีสิทธิ์ยาม ไม่ใช่ broadcast ทั้งโรงเรียน
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });

    // ส่งรหัสจอ/ประตูไปด้วย เซิร์ฟเวอร์จะได้ใส่เข้าห้องของประตูนั้น ไม่ใช่ห้องรวม
    socket.on('connect', () => socket.emit(
      'joinGateDisplay',
      { display: displayCode || undefined, gate: gateCode || undefined },
      (res) => setJoined(res || { ok: false, reason: 'error' })
    ));

    socket.on('gateScan', (payload) => {
      // ป้ายกล่องออนไลน์ต้องขยับทุกครั้งที่มีคนแตะ แม้จะไม่เอาผลขึ้นจอ — เป็นหลักฐานว่ากล่องยังทำงาน
      setDevice({ deviceId: payload.deviceId, at: Date.now() });
      if (scanningRef.current) { setHeldFromDevice(n => n + 1); return; }
      clearTimeout(clearTimer.current);
      setConfirmed(false);
      setResult(payload);
      const ttl = payload.confirmable ? (payload.expires_in || 0) * 1000 : 0;
      resetSoon(ttl || AUTO_CLEAR_MS, !!ttl);
    });

    socket.on('gateConfirm', (payload) => {
      if (payload.deviceId) setDevice({ deviceId: payload.deviceId, at: Date.now() });
      // ยืนยันจากกล่องต้องไม่ไปเปลี่ยนผลของบัตรที่ยามแตะด้วยมือถือให้กลายเป็น "บันทึกแล้ว"
      if (scanningRef.current) { setHeldFromDevice(n => n + 1); return; }
      clearTimeout(clearTimer.current);
      setConfirmed(true);
      setResult(r => (r ? { ...r, confirm_token: null, confirmable: false, confirmedByType: payload.confirmedByType, confirmedByName: payload.confirmedByName } : r));
      resetSoon();
    });

    socket.on('gateDevice', (payload) => setDevice({ deviceId: payload.deviceId, at: payload.at }));

    return () => socket.disconnect();
  }, [resetSoon, displayCode, gateCode]);

  const handleScan = useCallback((uid, { valid }) => {
    clearTimeout(clearTimer.current);
    setConfirmed(false);

    if (!valid) {
      setResult({ decision: 'unknown', reason: `อ่าน UID ผิดรูปแบบ (${uid || 'ว่าง'}) — ลองแตะใหม่` });
      resetSoon();
      return;
    }

    setBusy(true);
    axiosInstance.get(`/api/guard/gate-pass/scan?uid=${uid}`)
      .then(res => {
        setResult(res.data);
        const ttl = res.data?.confirm_token ? (res.data.expires_in || 0) * 1000 : 0;
        resetSoon(ttl || AUTO_CLEAR_MS, !!ttl);
      })
      .catch(err => {
        setResult({
          decision: 'deny',
          reason: err.response?.data?.message || 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ — ใช้หน้าค้นหาชื่อแทน'
        });
        resetSoon();
      })
      .finally(() => setBusy(false));
  }, [resetSoon]);

  const { scanning, error: nfcError, start: startNfc, stop: stopNfc } = useNfcReader(handleScan);

  // มือถือของยามใช้เป็นเครื่องอ่านบัตร กับใช้เป็นจอรับผลจากกล่องหน้าประตู พร้อมกันไม่ได้
  // ปล่อยไว้ผลของนักเรียนที่แตะกล่องจะเด้งทับบัตรที่ยามเพิ่งแตะกับมือ รวมถึงทับใบที่รอกดยืนยัน
  // แล้วตั๋วของใบนั้นหมดอายุไปเงียบๆ ยามจึงต้องกดยกเลิกสแกนเองเมื่อเลิกใช้เป็นเครื่องอ่าน
  useEffect(() => {
    scanningRef.current = scanning;
    if (!scanning) setHeldFromDevice(0);
  }, [scanning]);

  // ยืนยันด้วยตั๋วอายุสั้นที่ได้ตอนแตะ ไม่ได้ส่ง pass_id ขึ้นไปเอง
  // ทางเดียวกับที่กล่อง ESP32 ใช้ จะได้บันทึกลง tb_gate_scan_log แถวเดียวกัน
  const confirm = () => {
    const token = result?.confirm_token;
    const deviceId = result?.deviceId;
    if (!token && !deviceId) return;

    setBusy(true);
    axiosInstance.post('/api/guard/gate-scan/confirm', token ? { confirm_token: token } : { device_id: deviceId })
      .then(() => { setConfirmed(true); resetSoon(); })
      .catch(err => setResult(r => ({
        ...r,
        confirm_token: null,
        decision: 'deny',
        reason: err.response?.data?.message || 'บันทึกไม่สำเร็จ'
      })))
      .finally(() => setBusy(false));
  };

  const screen = useScreen();
  // FHD (1080) ได้รูป ~492px ชื่อ ~56px / HD ready (768) ได้รูป ~350px ชื่อ ~40px
  // เพดานกันไว้เผื่อจอ 4K ไม่ให้รูปโตจนล้น และพื้นล่างกันไว้เผื่อมือถือแนวนอนของยาม
  // SCREEN_SCALE คือตัวหมุนขนาดรวมทั้งจอ — ลด/เพิ่มที่นี่ที่เดียวโดยไม่ต้องแก้สัดส่วนทีละบรรทัด
  const sz = (ratio, min, max) =>
    Math.round(Math.min(Math.max(screen.h * ratio * SCREEN_SCALE, min), max * SCREEN_SCALE));
  const photoSize = sz(.48, 150, 580);
  const nameSize = sz(.055, 22, 64);
  const metaSize = sz(.028, 14, 32);
  const titleSize = sz(.06, 26, 68);
  const iconSize = sz(.075, 44, 96);
  const bodySize = sz(.032, 16, 36);
  // ทีวีรุ่นเก่าครอบขอบจอทิ้ง (overscan) ป้ายกล่องออนไลน์กับปุ่มมุมขวาจะหลุดขอบไปเลย
  const tvInset = screen.w >= 1280 && screen.h >= 700 ? '1.5vh 2vw' : 0;

  // ชื่อที่ขึ้นบนหัวจอ — เอาจากคำตอบของ server ก่อน (จอที่ลงทะเบียนไว้รู้ประตูจากทะเบียน)
  // แล้วค่อยเทียบรหัสกับรายการประตู เผื่อกรณีที่ยามเลือกประตูเองจากมือถือ
  const watchingGate = joined?.gateCode || gateCode;
  const gateLabel = joined?.gateName
    || gates.find(g => g.gate_code === watchingGate)?.gate_name
    || watchingGate
    || 'ทุกประตู';
  const joinError = joined && joined.ok === false
    ? (JOIN_ERROR[joined.reason] || JOIN_ERROR.error)
    : null;

  const theme = (result?.late ? LATE_THEME : THEME[result?.decision]) || null;
  const student = result?.student;
  const pass = result?.pass;
  const canConfirm = !confirmed && (!!result?.confirm_token || !!result?.confirmable);
  const deviceOnline = !!device && now - device.at < DEVICE_STALE_MS;
  const secondsLeft = deadline ? Math.max(0, Math.ceil((deadline - now) / 1000)) : 0;

  // เครื่องที่อ่านบัตรเองไม่ได้ (PC/จอติดผนัง) ต้องไม่บอกให้ "กดปุ่มด้านล่างเพื่อเริ่ม"
  // เพราะปุ่มนั้นมีไว้เปิด NFC ในตัวเครื่อง ซึ่งเดสก์ท็อปไม่มีทางใช้ได้
  const idle = nfcBlocked
    ? (deviceOnline
        ? { icon: 'ri-rfid-line', text: 'รอนักเรียนแตะบัตรที่กล่องหน้าประตู' }
        : { icon: 'ri-error-warning-line', text: 'เครื่องนี้อ่านบัตรเองไม่ได้ และยังไม่พบกล่องที่ประตู' })
    : { icon: 'ri-cellphone-line', text: scanning ? 'พร้อมแล้ว — แตะบัตรที่ด้านหลังเครื่อง' : 'กดปุ่มด้านล่างเพื่อเริ่ม' };

  return (
    <div className="d-flex flex-column vh-100" style={{ background: theme?.bg || '#12161c', transition: 'background .25s', padding: tvInset }}>

      <div className="d-flex align-items-center justify-content-between px-3 py-2 text-white-50"
        style={{ background: 'rgba(0,0,0,.25)', fontSize: metaSize }}>
        <span className="fw-semibold">
          <i className="ri-door-open-line me-1" />
          {gateLabel}
          {joined?.displayName && <span className="ms-1 opacity-75">· {joined.displayName}</span>}
        </span>
        <div className="d-flex align-items-center gap-2">
          {/* จอที่ลงทะเบียนไว้ห้ามสลับประตูจากตัวจอ — ผูกไว้ที่ทะเบียนแล้ว ไม่งั้นใครเดินไปกดที่ทีวี
              ก็เปลี่ยนได้ แล้ววันหลังไม่มีใครรู้ว่าจอนี้ควรโชว์ประตูไหน */}
          {!displayCode && gates.length > 1 && (
            <Form.Select
              size="sm" value={gateCode} onChange={e => changeGate(e.target.value)}
              style={{ width: 'auto', minWidth: 130 }} title="เลือกประตูที่จะรับผล"
            >
              <option value="">ทุกประตู</option>
              {gates.map(g => (
                <option key={g.gate_id} value={g.gate_code}>{g.gate_name}</option>
              ))}
            </Form.Select>
          )}
          <Button variant="outline-light" size="sm" onClick={toggleMenu}
            title={menuHidden ? 'แสดงเมนู' : 'ซ่อนเมนูให้เต็มจอ'}>
            <i className={`ri-${menuHidden ? 'menu-unfold' : 'menu-fold'}-line`} />
          </Button>
          {device && (
            <span className={`badge ${deviceOnline ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '.9em' }} title={`${device.deviceId} — เห็นล่าสุด ${new Date(device.at).toLocaleTimeString('th-TH')}`}>
              <i className={`ri-${deviceOnline ? 'wifi' : 'wifi-off'}-line me-1`} />
              กล่อง{deviceOnline ? 'ออนไลน์' : 'ไม่ตอบสนอง'}
            </span>
          )}
          <Link to="/guard/gate-pass" className="btn btn-sm btn-outline-light">
            <i className="ri-search-line me-1" />ค้นหาด้วยชื่อ
          </Link>
        </div>
      </div>

      {/* เข้าห้องรับผลไม่ได้ = จอนี้จะไม่ขึ้นอะไรเลยตอนนักเรียนแตะบัตร ต้องบอกให้เห็นเต็มตา
          ไม่ใช่ปล่อยให้ดูเหมือนว่ากล่องหน้าประตูเสีย */}
      {joinError && (
        <div className="alert alert-danger mb-0 rounded-0 py-2 text-center fs-sm">
          <i className="ri-error-warning-line me-1" />{joinError}
        </div>
      )}

      <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center text-white p-3 overflow-auto">

        {busy && <Spinner animation="border" variant="light" className="mb-3" />}

        {!result && !busy && (
          <>
            <i className={idle.icon} style={{ fontSize: sz(.11, 70, 190), opacity: .35 }} />
            <div className="mt-3 text-white-50" style={{ fontSize: bodySize }}>{idle.text}</div>
          </>
        )}

        {result && !busy && (
          <>
            <i className={theme.icon} style={{ fontSize: iconSize, color: theme.accent }} />
            <div className="fw-bold mt-2" style={{ fontSize: titleSize, color: theme.accent }}>
              {confirmed ? 'บันทึกแล้ว' : theme.title}
            </div>
            {confirmed && result.confirmedByType && (
              <div className="text-white-50" style={{ fontSize: metaSize }}>{CONFIRMED_BY_LABEL[result.confirmedByType] || ''}{result.confirmedByName ? ` — ${result.confirmedByName}` : ''}</div>
            )}

            {student && (
              <div className="d-flex flex-column flex-md-row align-items-center gap-3 gap-md-4 mt-3 px-4 py-3 rounded-4" style={{ background: 'rgba(0,0,0,.3)' }}>
                {photoUrl(student.st_photo) ? (
                  <img src={photoUrl(student.st_photo)} alt={student.st_name}
                    className="rounded-4 border border-3 bg-white flex-shrink-0"
                    style={{ width: photoSize, height: photoSize, objectFit: 'cover', borderColor: theme.accent }} />
                ) : (
                  <div className="rounded-4 bg-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: photoSize, height: photoSize, fontSize: Math.round(photoSize * .42) }}>
                    {student.st_name?.trim().charAt(0) || '?'}
                  </div>
                )}
                <div className="text-center text-md-start">
                  <div className="fw-bold" style={{ fontSize: nameSize }}>{student.st_name}</div>
                  <div className="text-white-50" style={{ fontSize: metaSize }}>
                    {student.st_idnumber}
                    {student.c_id && student.r_no ? ` · ม.${student.c_id}/${student.r_no}` : ''}
                  </div>
                  {pass && (
                    <div className="mt-1" style={{ fontSize: metaSize }}>
                      <span className="badge bg-light text-dark me-1">{PASS_LABEL[pass.pass_type]}</span>
                      {pass.return_date && (
                        <span className={result.late ? 'fw-semibold' : 'text-white-50'}>
                          กำหนดกลับ {thaiDate(pass.return_date)}
                        </span>
                      )}
                      {pass.expect_return_time && <span className="text-white-50">กลับเวลา {pass.expect_return_time}</span>}
                    </div>
                  )}
                  {/* ยามต้องรู้ว่าใครเป็นคนอนุญาต จะได้ตามถามถูกคนเวลานักเรียนอ้างว่าได้รับอนุญาตแล้ว
                      ใบที่ยังรอฝ่ายปกครองยังไม่มีผู้อนุญาต — เว้นบรรทัดนี้ไปเลย ดีกว่าขึ้นคำว่า "ไม่ระบุ" ให้เข้าใจผิดว่าอนุมัติแล้ว */}
                  {pass?.approved_by_name && (
                    <div className="mt-1" style={{ fontSize: metaSize }}>
                      <i className="ri-shield-user-line me-1" style={{ color: theme.accent }} />
                      อนุญาตโดย <span className="fw-semibold">{pass.approved_by_name}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {result.owner && (
              <div className="mt-2 text-white-50" style={{ fontSize: bodySize }}>{result.owner.name}</div>
            )}

            <div className="mt-3" style={{ fontSize: bodySize, maxWidth: Math.min(screen.w * .8, 1100) }}>{result.reason}</div>

            {canConfirm && (
              <Button
                size="lg" variant="light" className="mt-4 px-5 py-3 fw-bold"
                style={{ fontSize: bodySize }} disabled={busy} onClick={confirm}
              >
                <i className="ri-check-line me-2" />
                {result.decision === 'return' ? 'ยืนยันกลับเข้า' : 'ยืนยันออก'}
              </Button>
            )}

            {canConfirm && !!secondsLeft && (
              <div className="mt-2 text-white-50" style={{ fontSize: metaSize }}>เหลือเวลายืนยัน {secondsLeft} วิ</div>
            )}

            <Button variant="outline-light" size="sm" className="mt-3"
              onClick={() => {
                clearTimeout(clearTimer.current);
                setResult(null); setConfirmed(false); setDeadline(0);
              }}>
              ล้างจอ
            </Button>
          </>
        )}
      </div>

      <div className="p-3" style={{ background: 'rgba(0,0,0,.25)' }}>
        {nfcBlocked ? (
          deviceOnline ? (
            // อ่านบัตรจากกล่องอยู่แล้ว การที่เครื่องนี้ไม่มี NFC จึงเป็นเรื่องปกติ ไม่ใช่ปัญหา
            <div className="text-center text-white-50 fs-sm">
              <i className="ri-checkbox-circle-line me-1" />
              รับบัตรจากกล่องที่ประตู · เครื่องนี้ใช้แสดงผลอย่างเดียว
            </div>
          ) : (
            <div className="alert alert-warning mb-0 py-2 fs-sm">
              <b>เครื่องนี้อ่านบัตรเองไม่ได้:</b> {nfcBlocked} — <Link to="/guard/gate-pass">ใช้หน้าค้นหาด้วยชื่อแทน</Link>
            </div>
          )
        ) : (
          <>
            {nfcError && <div className="alert alert-danger py-2 fs-sm">{nfcError}</div>}
            {/* ต้องบอกว่าผลจากกล่องถูกพักไว้ ไม่ใช่ดักเงียบๆ ไม่งั้นยามจะเห็นแค่ว่ากล่องหน้าประตูเสีย
                นักเรียนที่แตะไปยังยืนรออยู่หน้าประตู และตั๋วยืนยันของใบนั้นก็เดินหมดอายุอยู่ */}
            {scanning && deviceOnline && (
              <div className="text-center text-white-50 fs-sm mb-2">
                <i className="ri-pause-circle-line me-1" />
                พักผลจากกล่องที่ประตูไว้ระหว่างใช้เครื่องนี้อ่านบัตร
                {heldFromDevice > 0 && ` — ข้ามไป ${heldFromDevice} ครั้ง`}
              </div>
            )}
            <Button
              variant={scanning ? 'outline-light' : 'primary'}
              className="w-100 py-3 fw-bold" style={{ fontSize: 18 }}
              onClick={scanning ? stopNfc : startNfc}
            >
              <i className={`ri-${scanning ? 'stop-circle' : 'cellphone'}-line me-2`} />
              {scanning ? 'ยกเลิกสแกน' : 'เริ่มสแกนบัตร'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
